// ===========================================
// Student Profile React Query Hooks
// ===========================================
// Custom hooks for student self-service API operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMyStudentProfile,
  getMyStudentEnrollments,
  getMyStudentAttendance,
  getMyStudentNotes,
  getMyStudentResources,
  updateMyStudentAvatar,
  StudentDashboardProfile,
  StudentEnrollment,
  StudentAttendanceRecord,
} from '../api/user.api';
import { Note } from '../api/notes.api';
import { Resource } from '../api/resources.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const studentProfileKeys = {
  all: ['studentProfile'] as const,
  profile: () => [...studentProfileKeys.all, 'profile'] as const,
  enrollments: () => [...studentProfileKeys.all, 'enrollments'] as const,
  attendance: (params?: { limit?: number; startDate?: string; endDate?: string }) =>
    [...studentProfileKeys.all, 'attendance', params] as const,
  notes: (params?: { lessonId?: string; startDate?: string; endDate?: string }) =>
    [...studentProfileKeys.all, 'notes', params] as const,
  resources: (params?: { lessonId?: string }) =>
    [...studentProfileKeys.all, 'resources', params] as const,
};

// ===========================================
// QUERIES
// ===========================================

/**
 * Get student's own profile
 */
export function useMyStudentProfile(options?: { enabled?: boolean }) {
  return useQuery<StudentDashboardProfile>({
    queryKey: studentProfileKeys.profile(),
    queryFn: async () => {
      const response = await getMyStudentProfile();
      return response.data;
    },
    enabled: options?.enabled,
  });
}

/**
 * Get student's own enrollments/classes
 */
export function useMyStudentEnrollments() {
  return useQuery<StudentEnrollment[]>({
    queryKey: studentProfileKeys.enrollments(),
    queryFn: async () => {
      const response = await getMyStudentEnrollments();
      return response.data;
    },
  });
}

/**
 * Get student's own attendance history
 */
export function useMyStudentAttendance(params?: {
  limit?: number;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery<StudentAttendanceRecord[]>({
    queryKey: studentProfileKeys.attendance(params),
    queryFn: async () => {
      const response = await getMyStudentAttendance(params);
      return response.data;
    },
  });
}

/**
 * Get notes shared with the student
 */
export function useMyStudentNotes(params?: {
  lessonId?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery<Note[]>({
    queryKey: studentProfileKeys.notes(params),
    queryFn: async () => {
      const response = await getMyStudentNotes(params);
      return response.data as Note[];
    },
  });
}

/**
 * Get resources shared with the student
 */
export function useMyStudentResources(params?: { lessonId?: string }) {
  return useQuery<Resource[]>({
    queryKey: studentProfileKeys.resources(params),
    queryFn: async () => {
      const response = await getMyStudentResources(params);
      return response.data as Resource[];
    },
  });
}

// ===========================================
// MUTATIONS
// ===========================================

/**
 * Update student's avatar
 */
export function useUpdateStudentAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (avatar: string | null) => updateMyStudentAvatar(avatar),
    onSuccess: (response) => {
      // Update cache with new profile data
      queryClient.setQueryData(studentProfileKeys.profile(), response.data);
      // Also invalidate to ensure all components re-render with fresh data
      queryClient.invalidateQueries({ queryKey: studentProfileKeys.profile() });
    },
  });
}
